import React from "react";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import ModalMain from "@dropDesk/presentation/components/modals/modal_main";
import DropDeskIcon from "@dropDesk/storage/icons";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import {
    BenefitCard,
    BenefitDesc,
    BenefitIconBox,
    BenefitTextGroup,
    BenefitTitle,
    Body,
    CloseButton,
    Container,
    FeatureDesc,
    FeatureIconRow,
    FeatureItem,
    FeatureName,
    FeaturesCard,
    FeaturesGrid,
    FeaturesTitle,
    Footer,
    FooterButtons,
    FooterNote,
    HeaderWrapper,
    IconBox,
    PrimaryButton,
    SecondaryButton,
    StatusBadge,
    StatusCard,
    StatusHint,
    StatusIconBox,
    StatusRow,
    StatusText,
    Subtitle,
    Title,
    TitleGroup,
    TitleRow,
} from "./styled";

const features = [
    {icon: ICONS_DROPDESK.internalChat, name: 'Conversar entre\natendentes', desc: 'Comunicação rápida\ne organizada'},
    {icon: ICONS_DROPDESK.clock, name: 'Histórico\ncentralizado', desc: 'Todas as conversas\nem um só lugar'},
    {icon: ICONS_DROPDESK.bellOutlined, name: 'Notificações em\ntempo real', desc: 'Não perca nenhuma\nmensagem importante'},
    {icon: ICONS_DROPDESK.paperclip, name: 'Troca de arquivos\ne links', desc: 'Compartilhe arquivos\ncom facilidade'},
];

const ModalInternalChatUpsell = (
    {
        open,
        isAdmin,
        onActivate,
        onClose,
    }: {
        open: boolean;
        isAdmin: boolean;
        onActivate: () => void;
        onClose: () => void;
    }
) => {
    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;

    return (
        <ModalMain
            open={open}
            onRequestClose={onClose}
            width={560}
            renderComponent={
                <Container background={colors.onBackground}>
                    <HeaderWrapper>
                        <CloseButton
                            background={colors.buttonOnBackground}
                            color={colors.text}
                            onClick={onClose}
                        >
                            <DropDeskIcon icon={ICONS_DROPDESK.close} size={14} color={colors.text}/>
                        </CloseButton>
                        <TitleRow>
                            <IconBox
                                background={`${colors.accent}18`}
                                border={`${colors.accent}30`}
                            >
                                <DropDeskIcon icon={ICONS_DROPDESK.internalChat} size={22} color={colors.accent}/>
                            </IconBox>
                            <TitleGroup>
                                <Title color={colors.text}>O Chat Interno não está ativo</Title>
                                <Subtitle color={colors.hint}>
                                    Esta é uma extensão premium e ainda não está disponível na sua assinatura.
                                </Subtitle>
                            </TitleGroup>
                        </TitleRow>
                    </HeaderWrapper>

                    <Body>
                        <FeaturesCard
                            background={colors.background}
                            border={colors.border}
                        >
                            <FeaturesTitle color={colors.accent}>
                                Com o Chat Interno sua equipe pode:
                            </FeaturesTitle>
                            <FeaturesGrid>
                                {features.map((f) => (
                                    <FeatureItem key={f.name}>
                                        <FeatureIconRow>
                                            <DropDeskIcon icon={f.icon} size={20} color={colors.accent}/>
                                        </FeatureIconRow>
                                        <FeatureName color={colors.text}>
                                            {f.name.split('\n').map((line, i) => (
                                                <React.Fragment key={i}>{line}{i === 0 && <br/>}</React.Fragment>
                                            ))}
                                        </FeatureName>
                                        <FeatureDesc color={colors.hint}>
                                            {f.desc.split('\n').map((line, i) => (
                                                <React.Fragment key={i}>{line}{i === 0 && <br/>}</React.Fragment>
                                            ))}
                                        </FeatureDesc>
                                    </FeatureItem>
                                ))}
                            </FeaturesGrid>
                        </FeaturesCard>

                        <StatusCard
                            background={`${colors.warning}10`}
                            border={`${colors.warning}25`}
                        >
                            <StatusIconBox
                                background={`${colors.warning}20`}
                                border={`${colors.warning}35`}
                            >
                                <DropDeskIcon icon={ICONS_DROPDESK.padlock} size={18} color={colors.warning}/>
                            </StatusIconBox>
                            <div>
                                <StatusRow>
                                    <StatusText color={colors.text}>Status atual:</StatusText>
                                    <StatusBadge
                                        background={`${colors.warning}25`}
                                        color={colors.warning}
                                    >
                                        Não contratado
                                    </StatusBadge>
                                </StatusRow>
                                <StatusHint color={colors.hint}>
                                    Ative agora e ganhe 7 dias grátis para testar.
                                </StatusHint>
                            </div>
                        </StatusCard>

                        <BenefitCard
                            background={`${colors.success}10`}
                            border={`${colors.success}25`}
                        >
                            <BenefitIconBox
                                background={`${colors.success}20`}
                                border={`${colors.success}35`}
                            >
                                <DropDeskIcon icon={ICONS_DROPDESK.check} size={18} color={colors.success}/>
                            </BenefitIconBox>
                            <BenefitTextGroup>
                                <BenefitTitle color={colors.text}>Mais produtividade para sua equipe</BenefitTitle>
                                <BenefitDesc color={colors.hint}>
                                    Centralize a comunicação interna, ganhe agilidade e melhore a colaboração.
                                </BenefitDesc>
                            </BenefitTextGroup>
                        </BenefitCard>
                    </Body>

                    <Footer background={colors.onBackground}>
                        <FooterButtons>
                            {isAdmin && (
                                <PrimaryButton
                                    background={colors.accent}
                                    color={colors.textOnAccent}
                                    onClick={() => {
                                        onActivate();
                                        onClose();
                                    }}
                                >
                                    <DropDeskIcon icon={ICONS_DROPDESK.star} size={14} color={colors.textOnAccent}/>
                                    Ativar grátis por 7 dias
                                </PrimaryButton>
                            )}
                            {!isAdmin && (
                                <PrimaryButton
                                    background={colors.accent}
                                    color={colors.textOnAccent}
                                    onClick={onClose}
                                >
                                    OK
                                </PrimaryButton>
                            )}
                            <SecondaryButton
                                border={colors.border}
                                color={colors.text}
                                onClick={onClose}
                            >
                                Agora não
                            </SecondaryButton>

                        </FooterButtons>
                        {isAdmin && (
                            <FooterNote color={colors.hint}>
                                Cancele quando quiser. Sem compromisso.
                            </FooterNote>
                        )}
                    </Footer>
                </Container>
            }
        />
    );
};

export default ModalInternalChatUpsell;
